import React from 'react'
import { optimizeLabelImage } from '../utils/optimizeLabelImage'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Alert,
  LinearProgress,
  useMediaQuery,
} from '@mui/material'
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CleaningServicesRoundedIcon from '@mui/icons-material/CleaningServicesRounded'
import ContentPasteSearchRoundedIcon from '@mui/icons-material/ContentPasteSearchRounded'
import DrawRoundedIcon from '@mui/icons-material/DrawRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import ImageRoundedIcon from '@mui/icons-material/ImageRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded'
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded'
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded'
import RuleRoundedIcon from '@mui/icons-material/RuleRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import {
  createQualitySourceLine,
  createQualityControl,
  getQualityControls,
  getQualityControl,
  getQualityOrders,
  prepareQualityOrderLine,
  getQualityStatuses,
  updateQualityControl,
  updateQualityControlStatus,
  uploadFile,
  getFileDataUrl,
} from '../api/suiteApi'
import { AnimatedSection, AnimatedTableRow } from '../components/animations/MotionContainers'
import { useSuiteSession } from '../context/SuiteSessionContext'
import { APPLICATION_CODES } from '../data'
import ClasolLogo from '../media/logo_clasol-trasparente 1.png'
import QualityProductionLogo from '../media/CALIDAD_PRODUCCION.png'
import { getApplicationAccess, getQualityCapabilities, getUsuarioFromSession } from '../utils/permisos'
import { getWarehouseLabel } from '../utils/warehouses'
import { buildQualityPdfHtml } from '../utils/qualityPdf'
import { SignatureDialog } from '../components/quality/SignatureDialog'
import { useUnsavedChanges } from '../context/UnsavedChangesContext'

const fieldDefaults = {
  certifiedProduct: 'si',
  palletType: 'Europeo 120x80',
  packages: '48',
  boxType: 'Caja 40x30',
  caliber: '4',
  realCaliber: '4',
  band: 'BANDA 2',
  realVariety: 'NAVELINA',
  minWeight: '250,00',
  packagingStatus: 'ok',
  meshLabelStatus: 'ok',
  boxLabelStatus: 'ok',
  orderedPallets: '20',
  totalExtension: '2',
  lineNotes: '',
  qualityNotes: '',
  qualityOwner: '',
  reviewer: '',
}

const sourceLineFormDefaults = {
  sourceDocumentNo: '',
  sourceLineNo: '10000',
  orderNo: '',
  lineNo: '10000',
  customerCode: '',
  customerName: '',
  destinationCode: '',
  destinationName: '',
  itemCode: '',
  itemName: '',
  varietyCode: '',
  varietyName: '',
  confectionCode: '',
  confectionName: '',
  brand: '',
  warehouseCode: '',
  warehouseName: '',
  plannedDepartureDate: '',
  orderedPallets: '',
  packages: '',
  status: 'PENDING',
}

const samplingRules = [
  { format: 'Mallas', rowSpan: 3, weight: 'Menor o igual 2kg', pallets: 'cualquiera', samples: '3 bolsas/palet' },
  { weight: 'Mayor a 2 kg', pallets: 'Hasta 5', samples: '3 bolsas/palet' },
  { weight: '', pallets: 'Mas de 5', samples: '2 bolsas/palet' },
  { format: 'Granel y encajado', rowSpan: 3, weight: 'Menor o igual a 2.3kg', pallets: 'Cualquiera', samples: '2 cajas/palet' },
  { weight: 'Mayor a 2.3 kg', pallets: 'Hasta 3', samples: '1 caja/palet' },
  { weight: '', pallets: 'Mas de 3', samples: '1 cajas/2 palet' },
]

const statusClass = {
  Pendiente: 'admin-status-warning',
  Controlado: 'admin-status-success',
  Borrador: 'admin-status-warning',
  'Pendiente revision': 'admin-status-warning',
  Completado: 'admin-status-success',
  'Con incidencia': 'admin-status-error',
  Cancelado: 'admin-status-error',
}

const backendStatusLabel = {
  DRAFT: 'Borrador',
  PENDING_REVIEW: 'Pendiente revision',
  COMPLETED: 'Completado',
  WITH_INCIDENT: 'Con incidencia',
  CANCELLED: 'Cancelado',
}

const sourceLineStatusLabel = {
  PENDING: 'Pendiente',
  IN_PROGRESS: 'En control',
  CONTROLLED: 'Controlado',
  COMPLETED: 'Controlado',
  WITH_INCIDENT: 'Con incidencia',
  CANCELLED: 'Cancelado',
}

const responseList = (response) => (
  Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
      ? response
      : []
)

const apiMessage = (error, fallback) => (
  error?.payload?.message ||
  error?.message ||
  fallback
)

const normalizeSourceLine = (line) => ({
  id: line.id,
  sourceLineId: line.navSource ? line.sourceLineId : line.id,
  navSource: Boolean(line.navSource),
  rawData: line.rawData || {},
  sourceDocumentNo: line.sourceDocumentNo,
  sourceLineNo: line.sourceLineNo,
  order: line.orderNo || line.sourceDocumentNo || '-',
  line: String(line.lineNo || line.sourceLineNo || ''),
  createdAt: formatDate(line.rawData?.fecha_creacion_business_central),
  departureAt: formatDate(line.plannedDepartureDate),
  customerCode: line.customerCode || '',
  client: line.customerName || '-',
  destinationCode: line.destinationCode || '',
  destination: line.destinationName || '-',
  itemCode: line.itemCode || '',
  itemName: line.itemName || '',
  varietyCode: line.varietyCode || '',
  confection: line.confectionName || '-',
  confectionCode: line.confectionCode || '',
  variety: line.varietyName || '-',
  brand: line.brand || '-',
  warehouseCode: line.warehouseCode || '',
  warehouseName: line.warehouseName || '',
  orderedPallets: line.orderedPallets || '',
  packages: line.packages || '',
  sourceStatusCode: line.status || 'PENDING',
  status: sourceLineStatusLabel[line.status] || backendStatusLabel[line.status] || line.status || 'Pendiente',
})

const buildSourceLinePayload = (form) => ({
  sourceSystem: 'NAVISION',
  sourceDocumentNo: form.sourceDocumentNo || form.orderNo,
  sourceLineNo: Number(form.sourceLineNo || form.lineNo) || 10000,
  orderNo: form.orderNo || form.sourceDocumentNo,
  lineNo: Number(form.lineNo || form.sourceLineNo) || 10000,
  customerCode: form.customerCode || undefined,
  customerName: form.customerName || undefined,
  destinationCode: form.destinationCode || undefined,
  destinationName: form.destinationName || undefined,
  itemCode: form.itemCode || undefined,
  itemName: form.itemName || undefined,
  varietyCode: form.varietyCode || undefined,
  varietyName: form.varietyName || undefined,
  confectionCode: form.confectionCode || undefined,
  confectionName: form.confectionName || undefined,
  brand: form.brand || undefined,
  warehouseCode: form.warehouseCode || undefined,
  warehouseName: form.warehouseName || undefined,
  plannedDepartureDate: form.plannedDepartureDate || undefined,
  orderedPallets: form.orderedPallets || undefined,
  packages: form.packages || undefined,
  status: form.status || 'PENDING',
})

const formatDate = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('es-ES')
}

const toIsoDate = (value) => {
  if (!value || value === '-') return undefined
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const match = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  return match ? `${match[3]}-${match[2]}-${match[1]}` : undefined
}

const warehouseParts = (value = '') => {
  const [code, ...nameParts] = String(value).split(' - ')
  return {
    warehouseCode: code?.trim() || undefined,
    warehouseName: nameParts.join(' - ').trim() || value || undefined,
  }
}

const okNoOkLabel = (value) => (value === 'ok' ? 'OK' : 'No OK')

const requiresSystemMm = (customerCode) => (
  ['2292', '2310', '2309'].includes(String(customerCode || '').trim())
)

const buildQualityControlPayload = ({
  selectedLine,
  warehouse,
  moment,
  form,
  photos,
  signatureDataUrl,
  statusCode,
  result,
  previousControl,
}) => {
  const warehouseInfo = warehouseParts(warehouse)
  const photoRows = [
    {
      photoType: 'MESH_LABEL',
      label: 'Etiqueta de la malla',
      fileId: photos.meshLabel?.apiFile?.id,
    },
    {
      photoType: 'BOX_LABEL',
      label: 'Etiqueta de la caja',
      fileId: photos.boxLabel?.apiFile?.id,
    },
  ].filter((photo) => photo.fileId)

  return {
    sourceLineId: selectedLine.sourceLineId,
    sourceDocumentNo: selectedLine.sourceDocumentNo || selectedLine.order,
    sourceLineNo: Number(selectedLine.sourceLineNo || selectedLine.line) || undefined,
    orderNo: selectedLine.order,
    lineNo: Number(selectedLine.line) || undefined,
    customerCode: selectedLine.customerCode,
    customerName: selectedLine.client,
    destinationCode: selectedLine.destinationCode,
    destinationName: selectedLine.destination,
    itemCode: selectedLine.itemCode,
    itemName: selectedLine.itemName,
    varietyCode: selectedLine.varietyCode,
    varietyName: selectedLine.variety,
    confectionCode: selectedLine.confectionCode,
    confectionName: selectedLine.confection,
    brand: selectedLine.brand,
    warehouseCode: selectedLine.warehouseCode || warehouseInfo.warehouseCode,
    warehouseName: selectedLine.warehouseName || warehouseInfo.warehouseName,
    plannedDepartureDate: toIsoDate(selectedLine.departureAt),
    statusCode,
    controlMoment: moment?.toUpperCase(),
    result,
    notes: form.qualityNotes,
    detail: {
      certifiedProduct: form.certifiedProduct,
      palletType: form.palletType,
      packages: form.packages,
      boxType: form.boxType,
      caliber: form.caliber,
      realCaliber: form.realCaliber,
      band: form.band,
      realVariety: form.realVariety,
      minWeight: form.minWeight,
      packagingStatus: form.packagingStatus,
      orderedPallets: form.orderedPallets,
      totalExtension: form.totalExtension,
      lineNotes: form.lineNotes,
      qualityNotes: form.qualityNotes,
    },
    checks: [
      {
        section: 'PRODUCT',
        code: 'PACKAGING_STATUS',
        label: 'Estado envase',
        value: form.packagingStatus,
        result: form.packagingStatus === 'ok' ? 'OK' : 'INCIDENT',
        displayOrder: 10,
      },
      {
        section: 'LABELING',
        code: 'MESH_LABEL_OK',
        label: 'Etiqueta de la malla',
        value: form.meshLabelStatus,
        result: form.meshLabelStatus === 'ok' ? 'OK' : 'INCIDENT',
        displayOrder: 20,
      },
      {
        section: 'LABELING',
        code: 'BOX_LABEL_OK',
        label: 'Etiqueta de la caja',
        value: form.boxLabelStatus,
        result: form.boxLabelStatus === 'ok' ? 'OK' : 'INCIDENT',
        displayOrder: 30,
      },
    ],
    photos: [
      ...(previousControl?.photos || []).filter((photo) => !['MESH_LABEL', 'BOX_LABEL'].includes(photo.photoType))
        .map(({ fileId, photoType, label, description, displayOrder }) => ({ fileId, photoType, label, description, displayOrder })),
      ...photoRows,
    ],
    signatures: signatureDataUrl
      ? [
          ...(previousControl?.signatures || []).filter((signature) => !['RESPONSIBLE', 'REVIEWER'].includes(signature.signatureType))
            .map(({ signatureType, signedByName, signedByUserId, fileId, signatureData }) => ({ signatureType, signedByName, signedByUserId: signedByUserId || undefined, fileId: fileId || undefined, signatureData })),
          {
            signatureType: 'RESPONSIBLE',
            signedByName: form.qualityOwner,
            signatureData: { dataUrl: signatureDataUrl },
          },
        ]
      : (previousControl?.signatures || []).filter((signature) => signature.signatureType !== 'REVIEWER').map(({ signatureType, signedByName, signedByUserId, fileId, signatureData }) => ({ signatureType, signedByName, signedByUserId: signedByUserId || undefined, fileId: fileId || undefined, signatureData })),
  }
}

const lineMatchesControl = (line, control) => (
  (Boolean(control.sourceLineId && line.sourceLineId) && String(control.sourceLineId) === String(line.sourceLineId)) ||
  (
    String(control.sourceDocumentNo || control.orderNo || '') === String(line.sourceDocumentNo || line.order || '') &&
    String(control.sourceLineNo || control.lineNo || '') === String(line.sourceLineNo || line.line || '')
  )
)

const formFromControl = (control, fallbackForm) => {
  const detail = control?.detail || {}
  const checkValue = (code, fallback) => {
    const check = (control?.checks || []).find((item) => item.code === code)
    return check?.value || fallback
  }

  return {
    ...fallbackForm,
    certifiedProduct: detail.certifiedProduct || fallbackForm.certifiedProduct,
    palletType: detail.palletType || fallbackForm.palletType,
    packages: detail.packages || fallbackForm.packages,
    boxType: detail.boxType || fallbackForm.boxType,
    caliber: detail.caliber || fallbackForm.caliber,
    realCaliber: detail.realCaliber || fallbackForm.realCaliber,
    band: detail.band || fallbackForm.band,
    realVariety: detail.realVariety || fallbackForm.realVariety,
    minWeight: detail.minWeight || fallbackForm.minWeight,
    packagingStatus: checkValue('PACKAGING_STATUS', detail.packagingStatus || fallbackForm.packagingStatus),
    meshLabelStatus: checkValue('MESH_LABEL_OK', fallbackForm.meshLabelStatus),
    boxLabelStatus: checkValue('BOX_LABEL_OK', fallbackForm.boxLabelStatus),
    orderedPallets: detail.orderedPallets || fallbackForm.orderedPallets,
    totalExtension: detail.totalExtension || fallbackForm.totalExtension,
    lineNotes: detail.lineNotes || fallbackForm.lineNotes,
    qualityNotes: detail.qualityNotes || control?.notes || fallbackForm.qualityNotes,
    qualityOwner: control?.responsibleUser?.displayName || control?.signatures?.[0]?.signedByName || fallbackForm.qualityOwner,
    reviewer: control?.reviewedBy?.displayName || '',
  }
}

const certifiedProductLabel = {
  no: 'No',
  si: 'Si',
  igp: 'IGP',
  global: 'GLOBAL',
}

const momentLabel = {
  inicio: 'Inicio',
  durante: 'Durante',
  cambios: 'Cambios',
  final: 'Final',
}

const QualitySection = ({ title, icon, children, className = '' }) => (
  <AnimatedSection>
    <Card className={`quality-section-card ${className}`}>
      <CardContent>
        <Box className="quality-section-title-row">
          <Box className="quality-section-icon">{icon}</Box>
          <Typography className="section-title">{title}</Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  </AnimatedSection>
)

const editorSnapshot = ({ form, moment, signatureDataUrl, photos }) => JSON.stringify({
  form, moment, signatureDataUrl,
  photos: Object.fromEntries(Object.entries(photos).map(([key, photo]) => [key, photo ? {
    fileId: photo.apiFile?.id, name: photo.name,
    localPreview: photo.file ? photo.previewUrl : undefined,
  } : null])),
})

const PhotoDropzone = ({ title, value, onChange, onUpload }) => {
  const cameraInputId = React.useId()
  const galleryInputId = React.useId()
  const uploadRequest = React.useRef(0)
  React.useEffect(() => () => { uploadRequest.current += 1 }, [])

  const uploadPhoto = async (photo) => {
    const requestId = ++uploadRequest.current
    onChange({ ...photo, uploadError: undefined, uploadStatus: onUpload ? 'uploading' : 'local' })

    if (!onUpload) return

    try {
      const file = await optimizeLabelImage(photo.file)
      if (requestId !== uploadRequest.current) return
      if (file !== photo.file) {
        photo = { ...photo, file, previewUrl: URL.createObjectURL(file), name: file.name }
        onChange({ ...photo, uploadError: undefined, uploadStatus: 'uploading' })
      }
      const uploadedFile = await onUpload(photo.file)
      if (requestId !== uploadRequest.current) return
      onChange({
        ...photo,
        apiFile: uploadedFile,
        uploadStatus: 'uploaded',
      })
    } catch (error) {
      if (requestId !== uploadRequest.current) return
      onChange({
        ...photo,
        uploadError: error,
        uploadStatus: 'error',
      })
    }
  }

  const handleFileChange = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    event.target.value = ''
    uploadPhoto({ file, previewUrl: URL.createObjectURL(file), name: file.name })
  }

  const handleRemove = () => {
    uploadRequest.current += 1
    onChange(null)
  }

  return (
  <Box className="quality-photo-box" role="group" aria-label={title}>
    <Typography>{title}</Typography>
    <Box className={`quality-photo-drop${value?.previewUrl ? ' quality-photo-drop-filled' : ''}`}>
      {value?.previewUrl ? (
        <img src={value.previewUrl} alt={title} />
      ) : (
        <>
          <CameraAltRoundedIcon />
          <Typography>Sin imagen</Typography>
          <Typography>Haz una foto o adjunta una imagen</Typography>
        </>
      )}
    </Box>
    {value?.uploadStatus && (
      <Typography className={`quality-photo-upload-status quality-photo-upload-${value.uploadStatus}`}>
        {value.uploadStatus === 'uploading' && 'Subiendo imagen...'}
        {value.uploadStatus === 'uploaded' && 'Imagen subida a Suite API'}
        {value.uploadStatus === 'error' && `No se pudo subir: ${apiMessage(value.uploadError, 'Error al adjuntar la imagen')}. Reintenta la subida o quita la imagen.`}
        {value.uploadStatus === 'local' && 'Imagen pendiente de guardar'}
      </Typography>
    )}
    <Box className="quality-photo-actions">
      <Button component="label" variant="contained" startIcon={<CameraAltRoundedIcon />}>
        Hacer foto
        <input
          id={cameraInputId}
          aria-label={`Hacer foto de ${title.toLowerCase()}`}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={handleFileChange}
        />
      </Button>
      <Button component="label" variant="outlined" startIcon={<ImageRoundedIcon />}>
        Galeria
        <input
          id={galleryInputId}
          aria-label={`Adjuntar ${title.toLowerCase()} desde galeria`}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileChange}
        />
      </Button>
      {value?.uploadStatus === 'error' && onUpload ? <Button variant="outlined" onClick={() => uploadPhoto(value)}>Reintentar subida</Button> : null}
      {value ? <Button color="error" onClick={handleRemove}>Quitar imagen</Button> : null}
    </Box>
  </Box>
  )
}

const LineCard = ({ line, selected, onSelect }) => (
  <Box className={`quality-line-card${selected ? ' quality-line-card-selected' : ''}`} onClick={onSelect}
    role="button" tabIndex={0} aria-pressed={selected}
    onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect() } }}>
    <Box>
      <Typography className="quality-line-order">{line.order}</Typography>
      <Typography className="quality-line-meta">Linea {line.line} · Salida {line.departureAt}</Typography>
    </Box>
    <Chip size="small" className={`admin-status-chip ${statusClass[line.status]}`} label={line.status} />
    <Typography className="quality-line-client">{line.client}</Typography>
    <Typography className="quality-line-product">{line.confection}</Typography>
    <Box className="quality-line-footer">
      <Typography>{line.variety}</Typography>
      <Typography>{line.brand}</Typography>
    </Box>
  </Box>
)

const SourceLineDialog = ({ open, form, saving, onChange, onClose, onSubmit }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="quality-mobile-dialog">
    <DialogTitle>Nueva linea origen de prueba</DialogTitle>
    <DialogContent dividers>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth size="small" label="Pedido" name="orderNo" value={form.orderNo} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField fullWidth size="small" label="Linea" name="lineNo" value={form.lineNo} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField fullWidth size="small" label="Fecha salida" name="plannedDepartureDate" type="date" value={form.plannedDepartureDate} onChange={onChange} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" label="Cod. cliente" name="customerCode" value={form.customerCode} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <TextField fullWidth size="small" label="Cliente" name="customerName" value={form.customerName} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" label="Cod. destino" name="destinationCode" value={form.destinationCode} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <TextField fullWidth size="small" label="Destino" name="destinationName" value={form.destinationName} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" label="Cod. articulo" name="itemCode" value={form.itemCode} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <TextField fullWidth size="small" label="Articulo" name="itemName" value={form.itemName} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" label="Cod. variedad" name="varietyCode" value={form.varietyCode} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <TextField fullWidth size="small" label="Variedad" name="varietyName" value={form.varietyName} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" label="Cod. confeccion" name="confectionCode" value={form.confectionCode} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <TextField fullWidth size="small" label="Confeccion" name="confectionName" value={form.confectionName} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" label="Marca" name="brand" value={form.brand} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" label="Cod. almacen" name="warehouseCode" value={form.warehouseCode} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" label="Almacen" name="warehouseName" value={form.warehouseName} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" label="Palets" name="orderedPallets" value={form.orderedPallets} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" label="Bultos" name="packages" value={form.packages} onChange={onChange} />
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancelar</Button>
      <Button variant="contained" onClick={onSubmit} disabled={saving || !form.orderNo}>
        Guardar linea
      </Button>
    </DialogActions>
  </Dialog>
)

const QualityCheckToggle = ({ label, value, onChange }) => (
  <Box className="quality-touch-check">
    <Typography>{label}</Typography>
    <ToggleButtonGroup
      exclusive
      value={value}
      onChange={(_, nextValue) => nextValue && onChange(nextValue)}
    >
      <ToggleButton value="ok">OK</ToggleButton>
      <ToggleButton value="no-ok">No OK</ToggleButton>
    </ToggleButtonGroup>
  </Box>
)

export const QualityProduction = ({ controlId } = {}) => {
  const isPhone = useMediaQuery('(max-width:599.95px)')
  const [mobileQueueOpen, setMobileQueueOpen] = React.useState(true)
  const controlPanelRef = React.useRef(null)
  const queuePanelRef = React.useRef(null)
  const { session, status: sessionStatus } = useSuiteSession()
  const { canRead, canWrite, canAdmin, canUpload } = getQualityCapabilities(session)
  const currentUser = getUsuarioFromSession(session)
  const hasQualityBackendAccess = Boolean(getApplicationAccess(session, APPLICATION_CODES.qualityProduction))
  const [sourceLines, setSourceLines] = React.useState([])
  const [statusOptions, setStatusOptions] = React.useState([])
  const [warehouse, setWarehouse] = React.useState('')
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [page, setPage] = React.useState(0)
  const [ordersTotal, setOrdersTotal] = React.useState(0)
  const [warehouseOptions, setWarehouseOptions] = React.useState([])
  const [controlStatus, setControlStatus] = React.useState('todos')
  const [selectedLine, setSelectedLine] = React.useState({ id: '', order: '-', line: '', client: '-', destination: '-', variety: '-', confection: '-' })
  const [currentControl, setCurrentControl] = React.useState(null)
  const [moment, setMoment] = React.useState('durante')
  const [form, setForm] = React.useState(fieldDefaults)
  const [sourceLineDialogOpen, setSourceLineDialogOpen] = React.useState(false)
  const [sourceLineForm, setSourceLineForm] = React.useState(sourceLineFormDefaults)
  const [photos, setPhotos] = React.useState({
    meshLabel: null,
    boxLabel: null,
  })
  const [signatureDialogOpen, setSignatureDialogOpen] = React.useState(false)
  const [signatureDataUrl, setSignatureDataUrl] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [selectionLoading, setSelectionLoading] = React.useState(false)
  const [message, setMessage] = React.useState(null)
  const photosRef = React.useRef(photos)
  const photoUploadsRef = React.useRef(new Map())
  const selectionRequest = React.useRef(0)
  const ordersRequest = React.useRef(0)
  const openedControlRef = React.useRef(null)
  const controlIsTerminal = Boolean(currentControl?.status?.terminal)
  const controlIsCompleted = currentControl?.statusCode === 'COMPLETED'
  const photosPending = Object.values(photos).some((photo) => photo && !photo.apiFile?.id)
  const awaitingReview = currentControl?.statusCode === 'PENDING_REVIEW'
  const editingBlocked = !canWrite || !selectedLine.id || selectionLoading || saving || (controlIsTerminal && !controlIsCompleted) || awaitingReview
  const saveBlocked = editingBlocked
  const [savedSnapshot, setSavedSnapshot] = React.useState(null)
  const editSnapshot = editorSnapshot({ form, moment, signatureDataUrl, photos })
  const hasUnsavedChanges = Boolean(selectedLine.id && savedSnapshot !== null && savedSnapshot !== editSnapshot)
  const confirmDiscard = useUnsavedChanges(hasUnsavedChanges || saving)
  React.useEffect(() => {
    if (selectedLine.id && !selectionLoading && savedSnapshot === null) setSavedSnapshot(editSnapshot)
  }, [selectedLine.id, selectionLoading, savedSnapshot, editSnapshot])

  // Let the mobile control mount before moving it into view. Desktop and tablet
  // keep their existing queue and form layout.
  React.useEffect(() => {
    if (!isPhone || mobileQueueOpen || !selectedLine.id) return undefined
    const frame = window.requestAnimationFrame(() => {
      controlPanelRef.current?.scrollIntoView?.({ block: 'start', behavior: 'auto' })
      controlPanelRef.current?.focus({ preventScroll: true })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [isPhone, mobileQueueOpen, selectedLine.id])

  const showMobileQueue = () => {
    setMobileQueueOpen(true)
    window.requestAnimationFrame(() => {
      queuePanelRef.current?.scrollIntoView?.({ block: 'start', behavior: 'auto' })
      queuePanelRef.current?.focus({ preventScroll: true })
    })
  }

  const loadQualityData = React.useCallback(async () => {
    const requestId = ++ordersRequest.current
    const [statusesResponse, linesResponse] = await Promise.all([
      getQualityStatuses(),
      getQualityOrders({ page: page + 1, limit: 25,
        search: debouncedSearch, warehouseCode: warehouse,
        status: controlStatus === 'todos' ? undefined : controlStatus,
      }),
    ])
    if (requestId !== ordersRequest.current) return []
    setStatusOptions(responseList(statusesResponse))
    const nextLines = responseList(linesResponse).map(normalizeSourceLine)
    setSourceLines(nextLines)
    setOrdersTotal(linesResponse?.meta?.total || 0)
    setWarehouseOptions(linesResponse?.warehouses || [])
    return nextLines
  }, [debouncedSearch, warehouse, page, controlStatus])

  React.useEffect(() => {
    if (search === debouncedSearch) return undefined
    const timer = setTimeout(() => { setDebouncedSearch(search); setPage(0) }, 350)
    return () => clearTimeout(timer)
  }, [search, debouncedSearch])

  React.useEffect(() => {
    photosRef.current = photos
  }, [photos])

  React.useEffect(() => () => {
    Object.values(photosRef.current).forEach((photo) => {
      if (photo?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl)
    })
  }, [])

  React.useEffect(() => {
    if (sessionStatus !== 'authenticated' || !hasQualityBackendAccess) return undefined

    let active = true
    setLoading(true)
    loadQualityData()
      .then(() => {
        if (!active) return
      })
      .catch((error) => {
        if (active) {
          setSourceLines([])
          setOrdersTotal(0)
          setMessage({ severity: 'error', text: apiMessage(error, 'No se han podido consultar los pedidos.') })
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
      ordersRequest.current += 1
    }
  }, [hasQualityBackendAccess, loadQualityData, sessionStatus])

  const handlePhotoChange = (key, photo) => {
    setPhotos((currentPhotos) => {
      if (currentPhotos[key]?.previewUrl?.startsWith('blob:') && currentPhotos[key].previewUrl !== photo?.previewUrl) {
        URL.revokeObjectURL(currentPhotos[key].previewUrl)
      }
      return {
        ...currentPhotos,
        [key]: photo,
      }
    })
  }

  const handlePhotoUpload = async (originalFile) => {
    const file = await optimizeLabelImage(originalFile)
    let pending = photoUploadsRef.current.get(file)
    if (!pending) {
      pending = uploadFile(file).then((uploaded) => {
        if (!uploaded?.id) throw new Error('El servidor no ha devuelto el identificador de la imagen')
        return uploaded
      }).finally(() => { photoUploadsRef.current.delete(file) })
      photoUploadsRef.current.set(file, pending)
    }
    return pending
  }

  const loadExistingControlForLine = React.useCallback(async (line) => {
    if (!line || sessionStatus !== 'authenticated' || !hasQualityBackendAccess) {
      return null
    }

    const response = await getQualityControls({
      page: 1,
      limit: 100,
      search: line.order || line.sourceDocumentNo,
    })
    return responseList(response).find((control) => lineMatchesControl(line, control)) || null
  }, [hasQualityBackendAccess, sessionStatus])

  const handleSelectLine = React.useCallback(async (line, allowDuringSave = false, providedControl) => {
    if (saving && !allowDuringSave) return
    if (!allowDuringSave && !confirmDiscard()) return
    const requestId = ++selectionRequest.current
    setSavedSnapshot(null)
    setSelectionLoading(true)
    setSelectedLine(line)
    setCurrentControl(null)
    setForm({
      ...fieldDefaults,
      qualityOwner: currentUser?.displayName || currentUser?.display_name || currentUser?.username || '',
      palletType: line.rawData?.des_palet || '',
      boxType: line.rawData?.des_envase || '',
      caliber: line.rawData?.des_calibre || line.rawData?.cod_calibre || '',
      realCaliber: '', band: '', minWeight: '', totalExtension: '',
      orderedPallets: line.orderedPallets || '',
      packages: line.packages || '',
      realVariety: line.variety && line.variety !== '-' ? line.variety : '',
    })
    setMoment('durante')
    setSignatureDialogOpen(false)
    setSignatureDataUrl('')
    setPhotos({ meshLabel: null, boxLabel: null })
    Object.values(photosRef.current).forEach((photo) => {
      if (photo?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl)
    })

    try {
      const existingControl = providedControl || await loadExistingControlForLine(line)
      if (requestId !== selectionRequest.current) return
      if (existingControl) {
        setCurrentControl(existingControl)
        setSelectedLine((current) => ({ ...current, status: existingControl.status?.label || current.status }))
        setMoment(existingControl.controlMoment?.toLowerCase() || 'durante')
        setForm((currentForm) => formFromControl(existingControl, currentForm))
        const storedPhoto = async (type) => {
          const photo = existingControl.photos?.find((item) => item.photoType === type)
          return photo ? {
            apiFile: { id: photo.fileId }, name: photo.originalName,
            previewUrl: photo.fileUuid ? await getFileDataUrl(photo.fileUuid) : '',
            uploadStatus: 'uploaded',
          } : null
        }
        const [meshLabel, boxLabel] = await Promise.all([storedPhoto('MESH_LABEL'), storedPhoto('BOX_LABEL')])
        if (requestId !== selectionRequest.current) return
        setPhotos({ meshLabel, boxLabel })
        setSignatureDataUrl(existingControl.signatures?.find((signature) => signature.signatureType === 'RESPONSIBLE')?.signatureData?.dataUrl || '')
      }
      setSelectionLoading(false)
      setMobileQueueOpen(false)
    } catch (error) {
      if (requestId !== selectionRequest.current) return
      setSelectionLoading(false)
      setSelectedLine((current) => ({ ...current, id: '' }))
      setMessage({ severity: 'warning', text: apiMessage(error, 'No se ha podido consultar si la linea tiene un control guardado.') })
    }
  }, [saving, confirmDiscard, currentUser?.displayName, currentUser?.display_name, currentUser?.username, loadExistingControlForLine])

  React.useEffect(() => {
    if (!controlId || openedControlRef.current === controlId || sessionStatus !== 'authenticated' || !hasQualityBackendAccess) return undefined
    openedControlRef.current = controlId
    const requestId = ++selectionRequest.current
    let active = true
    setSelectionLoading(true)
    getQualityControl(controlId).then(async (control) => {
      if (!active || requestId !== selectionRequest.current) return
      const line = {
        ...normalizeSourceLine({ ...control, id: `control:${control.id}`, status: control.statusCode,
          orderedPallets: control.detail?.orderedPallets, packages: control.detail?.packages }),
        sourceLineId: control.sourceLineId || undefined,
      }
      await handleSelectLine(line, true, control)
    }).catch((error) => {
      if (!active || requestId !== selectionRequest.current) return
      setSelectionLoading(false)
      setMessage({ severity: 'error', text: apiMessage(error, 'No se ha podido cargar el control para editarlo.') })
    })
    return () => { active = false }
  }, [controlId, handleSelectLine, sessionStatus, hasQualityBackendAccess])

  const availableLines = sourceLines
  const filteredLines = availableLines

  const handleSaveControl = async (statusCode = 'DRAFT', result) => {
    if (saveBlocked) return
    if (controlIsCompleted && statusCode !== 'DRAFT') return
    setSaving(true)
    setMessage(null)
    try {
      // Missing labels are optional. Every selected image must be persisted
      // before linking it to the draft, sharing any upload already in flight.
      const savedPhotos = Object.fromEntries(await Promise.all(Object.entries(photos).map(async ([key, photo]) => {
        if (!photo || photo.apiFile?.id) return [key, photo]
        if (!canUpload) throw new Error('Tu usuario no tiene permiso para adjuntar imagenes. Quita la imagen pendiente o solicita el permiso FILES_WRITE.')
        if (!photo.file) throw new Error('Vuelve a adjuntar la imagen pendiente antes de guardar.')
        const apiFile = await handlePhotoUpload(photo.file)
        return [key, { ...photo, apiFile, uploadStatus: 'uploaded', uploadError: undefined }]
      })))
      setPhotos(savedPhotos)
      const sourceLine = selectedLine.navSource ? await prepareQualityOrderLine({
        sourceDocumentNo: selectedLine.sourceDocumentNo,
        sourceLineNo: Number(selectedLine.sourceLineNo),
      }) : null
      const sourceLineId = sourceLine?.id || selectedLine.sourceLineId
      const payload = buildQualityControlPayload({
        selectedLine: { ...selectedLine, sourceLineId },
        warehouse,
        moment,
        form,
        photos: savedPhotos,
        signatureDataUrl,
        statusCode,
        result,
        previousControl: currentControl,
      })
      // Validation is a separate operation with its own backend permission check.
      delete payload.statusCode
      if (controlIsCompleted && signatureDataUrl === ((currentControl.signatures || []).find(signature => signature.signatureType === 'RESPONSIBLE')?.signatureData?.dataUrl || '')) {
        // Correcting other data must not recreate an unchanged signature or its date.
        delete payload.signatures
      }
      const saved = currentControl?.id
        ? await updateQualityControl(currentControl.id, payload)
        : await createQualityControl({ ...payload, statusCode: 'DRAFT' })
      setCurrentControl(saved)
      setSavedSnapshot(editorSnapshot({ form, moment, signatureDataUrl, photos: savedPhotos }))
      let finalControl = saved
      if (statusCode !== 'DRAFT') {
        finalControl = await updateQualityControlStatus(saved.id, {
          statusCode,
          result,
          notes: form.qualityNotes,
        })
      }
      setCurrentControl(finalControl)
      setSelectedLine((line) => ({ ...line, sourceLineId, status: finalControl.status?.label || line.status }))
      setMessage({ severity: 'success', text: controlIsCompleted ? `Correccion de ${finalControl?.controlNo || ''} guardada. Se mantiene completado y el cambio queda registrado en el historial.` : `Control ${finalControl?.controlNo || ''} guardado.` })
      try {
        await loadQualityData()
      } catch (error) {
        setMessage({ severity: 'warning', text: 'Control guardado. No se ha podido actualizar el listado de pedidos.' })
      }
    } catch (error) {
      setMessage({ severity: 'error', text: apiMessage(error, 'No se ha podido guardar el control.') })
    } finally {
      setSaving(false)
    }
  }

  const handleFormChange = (event) => {
    const { name, value } = event.target
    setForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }))
  }

  const handleSourceLineFormChange = (event) => {
    const { name, value } = event.target
    setSourceLineForm((currentForm) => ({
      ...currentForm,
      [name]: value,
      ...(name === 'orderNo' && !currentForm.sourceDocumentNo ? { sourceDocumentNo: value } : {}),
      ...(name === 'lineNo' && !currentForm.sourceLineNo ? { sourceLineNo: value } : {}),
    }))
  }

  const handleCreateSourceLine = async () => {
    if (!confirmDiscard()) return
    setSaving(true)
    setMessage(null)
    try {
      const created = await createQualitySourceLine(buildSourceLinePayload(sourceLineForm))
      const normalized = normalizeSourceLine(created)
      await loadQualityData()
      await handleSelectLine(normalized, true)
      setSourceLineDialogOpen(false)
      setSourceLineForm(sourceLineFormDefaults)
      setMessage({ severity: 'success', text: 'Linea origen creada para pruebas.' })
    } catch (error) {
      setMessage({ severity: 'error', text: apiMessage(error, 'No se ha podido crear la linea origen.') })
    } finally {
      setSaving(false)
    }
  }

  const handleGeneratePdf = () => {
    const printWindow = window.open('', '_blank')
    if (!printWindow) return
    const reviewerSignature = (currentControl?.signatures || []).filter((signature) => signature.signatureType === 'REVIEWER').slice(-1)[0]
    const responsibleSignature = (currentControl?.signatures || []).find((signature) => signature.signatureType === 'RESPONSIBLE' && signature.signatureData?.dataUrl === signatureDataUrl)

    const infoRows = [
      ['Pedido', selectedLine.order],
      ['Linea', selectedLine.line],
      ['Almacen', selectedLine.warehouseName || (selectedLine.warehouseCode ? getWarehouseLabel(selectedLine.warehouseCode) : '-')],
      ['Cliente', selectedLine.client],
      ['Destino', selectedLine.destination],
      ['Fecha creacion', selectedLine.createdAt],
      ['Fecha salida', selectedLine.departureAt],
      ['Confeccion', selectedLine.confection],
      ['Variedad', selectedLine.variety],
      ['Marca', selectedLine.brand],
    ]

    const qualityRows = [
      ['Momento del control', momentLabel[moment]],
      ['Producto certificado', certifiedProductLabel[form.certifiedProduct]],
      ['Tipo de palet', form.palletType],
      ['N de bultos', form.packages],
      ['Tipo de caja', form.boxType],
      ['Calibre', form.caliber],
      ['Calibre real', form.realCaliber],
      ['Banda', form.band],
      ['Variedad real', form.realVariety],
      ['Peso min. palet', form.minWeight],
      ['Estado envase', okNoOkLabel(form.packagingStatus)],
      ['Etiqueta malla', okNoOkLabel(form.meshLabelStatus)],
      ['Etiqueta caja', okNoOkLabel(form.boxLabelStatus)],
      ['N palets pedido', form.orderedPallets],
      ['Total ampliacion', form.totalExtension],
    ]

    const html = buildQualityPdfHtml({
      title: 'Control calidad produccion - ' + selectedLine.order,
      subtitle: selectedLine.order + ' - Linea ' + selectedLine.line,
      logo: ClasolLogo, infoRows, qualityRows, samplingRules,
      systemMm: requiresSystemMm(selectedLine.customerCode),
      photos: [
        { label: 'Etiqueta de la malla', url: photos.meshLabel?.previewUrl },
        { label: 'Etiqueta de la caja', url: photos.boxLabel?.previewUrl },
      ],
      notes: [['Observ. de linea', form.lineNotes], ['Observaciones de calidad', form.qualityNotes]],
      signatures: [
        { label: 'Responsable', name: form.qualityOwner, image: signatureDataUrl, signedAt: responsibleSignature?.createdAt },
        { label: 'Revisado', name: reviewerSignature?.signedByName || form.reviewer, image: reviewerSignature?.signatureData?.dataUrl, signedAt: reviewerSignature?.createdAt },
      ],
    })

    printWindow.document.open()
    printWindow.document.write(html)
    printWindow.document.close()
  }

  return (
    <Box className="page-fade quality-page">
      <Box className="page-heading suite-page-heading-row">
        <Box className="quality-heading-brand">
          <img src={QualityProductionLogo} alt="Control de calidad en produccion" />
          <Box>
            <Typography variant="h4">Control de calidad en produccion</Typography>
            <Typography>Busca una linea, revisa etiquetas, valida producto y guarda el control desde planta.</Typography>
          </Box>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} className="quality-heading-actions">
          {canAdmin ? <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => setSourceLineDialogOpen(true)} disabled={saving || selectionLoading}>
            Linea prueba
          </Button> : null}
          <Button variant="outlined" startIcon={<PictureAsPdfRoundedIcon />} onClick={handleGeneratePdf} disabled={!selectedLine.id || selectionLoading}>Generar PDF</Button>
          {canWrite ? <Button className="quality-primary-action" variant="contained" startIcon={<SaveRoundedIcon />} onClick={() => handleSaveControl()} disabled={saveBlocked}>{controlIsCompleted ? 'Guardar cambios' : 'Guardar borrador'}</Button> : null}
        </Stack>
      </Box>
      {message ? (
        <Alert severity={message.severity} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      ) : null}
      {loading || saving || selectionLoading ? <LinearProgress /> : null}
      {!canRead ? <Alert severity="warning">No tienes permiso para consultar controles.</Alert> : null}

      <Box className="quality-toolbar">
        <TextField
          size="small"
          label="Buscar pedido"
          placeholder="Pedido, cliente o variedad"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="quality-search-input"
          slotProps={{
            input: { startAdornment: <SearchRoundedIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} /> },
          }}
        />
        <FormControl size="small" className="quality-filter" sx={{ width: { xs: '100%', sm: 240 }, minWidth: { xs: 0, sm: 240 }, flexShrink: 0 }}>
          <InputLabel>Almacen de produccion</InputLabel>
          <Select value={warehouse} label="Almacen de produccion" onChange={(event) => { setWarehouse(event.target.value); setPage(0) }}>
            <MenuItem value="">Todos</MenuItem>
            {warehouseOptions.filter((code) => String(code).trim() !== '0').map((code) => (
              <MenuItem key={code} value={code}>{getWarehouseLabel(code)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" className="quality-filter">
          <InputLabel id="quality-order-status-label">Estado del control</InputLabel>
          <Select labelId="quality-order-status-label" value={controlStatus} label="Estado del control" onChange={(event) => { setControlStatus(event.target.value); setPage(0) }}>
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="PENDING">Pendientes</MenuItem>
            <MenuItem value="COMPLETED">Controlados</MenuItem>
            {statusOptions.filter((status) => !['PENDING', 'CONTROLLED', 'COMPLETED'].includes(status.code)).map((status) => (
              <MenuItem key={status.code} value={status.code}>
                {status.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<CleaningServicesRoundedIcon />} onClick={() => { setControlStatus('todos'); setSearch(''); setWarehouse(''); setPage(0) }}>
          Limpiar filtros
        </Button>
      </Box>

      <Grid container spacing={2.1} className="quality-workspace">
        <Grid size={{ xs: 12, lg: 4, xl: 3.2 }} hidden={isPhone && !mobileQueueOpen && Boolean(selectedLine.id)}>
          <Box ref={queuePanelRef} tabIndex={-1} className="quality-mobile-anchor">
          <AnimatedSection>
            <Card className="quality-queue-card">
              <CardContent>
                <Box className="admin-users-list-header">
                  <Box>
                    <Typography className="section-title">Lineas para controlar</Typography>
                    <Typography className="quality-section-copy">Selecciona una linea y continua el control.</Typography>
                  </Box>
                  <Chip className="suite-status-chip" label={`${ordersTotal} lineas`} />
                </Box>

                {isPhone && selectedLine.id ? <Button fullWidth variant="outlined" className="quality-return-control" onClick={() => setMobileQueueOpen(false)}>Volver al control seleccionado</Button> : null}
                <Box className="quality-line-list">
                  {!filteredLines.length && !loading ? <Typography>No hay pedidos para los filtros indicados.</Typography> : null}
                  {filteredLines.map((line) => (
                    <LineCard
                      key={line.id}
                      line={line}
                      selected={selectedLine.id === line.id}
                      onSelect={() => handleSelectLine(line)}
                    />
                  ))}
                </Box>

                <TablePagination
                  component="div"
                  count={ordersTotal}
                  page={page}
                  rowsPerPage={25}
                  rowsPerPageOptions={[25]}
                  onPageChange={(_, nextPage) => setPage(nextPage)}
                  labelRowsPerPage="Filas por pagina:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                  getItemAriaLabel={(type) => ({ first: 'Primera pagina', last: 'Ultima pagina', next: 'Pagina siguiente', previous: 'Pagina anterior' }[type])}
                  className="quality-orders-pagination"
                />

                <Box className="activity-table-wrap quality-lines-table">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>N pedido</TableCell>
                        <TableCell>Linea</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Salida</TableCell>
                        <TableCell>Variedad</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLines.map((line) => (
                        <TableRow
                          component={AnimatedTableRow}
                          key={line.id}
                          hover
                          selected={selectedLine.id === line.id}
                          className="admin-clickable-row"
                          onClick={() => handleSelectLine(line)}
                        >
                          <TableCell>{line.order}</TableCell>
                          <TableCell>{line.line}</TableCell>
                          <TableCell>{line.client}</TableCell>
                          <TableCell>{line.departureAt}</TableCell>
                          <TableCell>{line.variety}</TableCell>
                          <TableCell>
                            <Chip size="small" className={`admin-status-chip ${statusClass[line.status]}`} label={line.status} />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small"><MoreVertRoundedIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
          </AnimatedSection>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, lg: 8, xl: 8.8 }}>
          <Box ref={controlPanelRef} tabIndex={-1} className="quality-mobile-anchor" aria-label="Control de la linea seleccionada">
          {!selectedLine.id ? (
            <Alert severity="info">Selecciona una linea de pedido para iniciar el control.</Alert>
          ) : (
          <>
          <AnimatedSection>
            <Card className="quality-active-line-card">
              <CardContent>
                <Box className="quality-active-line-header">
                  <Box>
                    <Typography className="quality-active-kicker">Linea seleccionada</Typography>
                    <Typography className="quality-active-title">{selectedLine.order} · Linea {selectedLine.line}</Typography>
                    <Typography className="quality-active-subtitle">{selectedLine.client} · {selectedLine.destination}</Typography>
                  </Box>
                  <Chip size="small" className={`admin-status-chip ${statusClass[selectedLine.status]}`} label={selectedLine.status} />
                </Box>
                {isPhone ? <Button variant="outlined" fullWidth className="quality-change-line" onClick={showMobileQueue} disabled={saving || selectionLoading}>Cambiar linea de pedido</Button> : null}

                <Box className="quality-active-summary">
                  <Box>
                    <Typography>Confeccion</Typography>
                    <Typography>{selectedLine.confection}</Typography>
                  </Box>
                  <Box>
                    <Typography>Variedad</Typography>
                    <Typography>{selectedLine.variety}</Typography>
                  </Box>
                  <Box>
                    <Typography>Fecha salida</Typography>
                    <Typography>{selectedLine.departureAt}</Typography>
                  </Box>
                  <Box>
                    <Typography>Marca</Typography>
                    <Typography>{selectedLine.brand}</Typography>
                  </Box>
                </Box>

              </CardContent>
            </Card>
          </AnimatedSection>

          {controlIsCompleted ? <Alert severity="info">Puedes corregir este control completado. Se conservaran la validacion y la firma del revisor; cada guardado registrara el autor y los cambios en el historial.</Alert> : null}
          {controlIsTerminal && !controlIsCompleted ? <Alert severity="info">Este control esta cerrado y se muestra en modo consulta.</Alert> : null}
          {awaitingReview ? <Alert severity="info">Control enviado a revision. El revisor debe devolverlo antes de realizar correcciones.</Alert> : null}
          {photosPending ? <Alert severity="info">Hay una imagen pendiente de subir. Al guardar se esperara a su subida; si falla, puedes reintentar o quitarla.</Alert> : null}
          <Box component="fieldset" disabled={editingBlocked} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
          <Grid container spacing={2.1} className="quality-control-grid">
            <Grid size={{ xs: 12, xl: 7 }}>
              <QualitySection title="1. Datos de pedido" icon={<ContentPasteSearchRoundedIcon />}>
                <Box className="quality-readonly-grid">
                  <Box><Typography>Almacen</Typography><Typography>{selectedLine.warehouseName || (selectedLine.warehouseCode ? getWarehouseLabel(selectedLine.warehouseCode) : '-')}</Typography></Box>
                  <Box><Typography>Cliente</Typography><Typography>{selectedLine.client}</Typography></Box>
                  <Box><Typography>Destino</Typography><Typography>{selectedLine.destination}</Typography></Box>
                  <Box><Typography>Fecha creacion</Typography><Typography>{selectedLine.createdAt}</Typography></Box>
                  <Box><Typography>Fecha salida</Typography><Typography>{selectedLine.departureAt}</Typography></Box>
                  <Box><Typography>Confeccion</Typography><Typography>{selectedLine.confection}</Typography></Box>
                </Box>
              </QualitySection>
            </Grid>

            <Grid size={{ xs: 12, xl: 5 }}>
              <QualitySection title="2. Momento del control" icon={<RuleRoundedIcon />}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={moment}
                  onChange={(_, value) => value && setMoment(value)}
                  className="quality-moment-group"
                >
                  <ToggleButton value="inicio">Inicio</ToggleButton>
                  <ToggleButton value="durante">Durante</ToggleButton>
                  <ToggleButton value="cambios">Cambios</ToggleButton>
                  <ToggleButton value="final">Final</ToggleButton>
                </ToggleButtonGroup>
              </QualitySection>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <QualitySection title="4. Producto y validaciones" icon={<Inventory2RoundedIcon />}>
                <Box className="quality-validation-grid quality-validation-grid-wide">
                  <FormControl size="small">
                    <InputLabel>Producto certificado</InputLabel>
                    <Select name="certifiedProduct" value={form.certifiedProduct} label="Producto certificado" onChange={handleFormChange}>
                      <MenuItem value="no">No</MenuItem>
                      <MenuItem value="si">Si</MenuItem>
                      <MenuItem value="igp">IGP</MenuItem>
                      <MenuItem value="global">GLOBAL</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label="Tipo de palet" name="palletType" value={form.palletType} onChange={handleFormChange} size="small" />
                  <TextField label="N de bultos" name="packages" value={form.packages} onChange={handleFormChange} size="small" />
                  <TextField label="Tipo de caja" name="boxType" value={form.boxType} onChange={handleFormChange} size="small" />
                  <TextField label="Calibre" name="caliber" value={form.caliber} onChange={handleFormChange} size="small" />
                  <TextField label="Calibre real" name="realCaliber" value={form.realCaliber} onChange={handleFormChange} size="small" />
                  <TextField label="Banda" name="band" value={form.band} onChange={handleFormChange} size="small" />
                  <TextField label="Variedad real" name="realVariety" value={form.realVariety} onChange={handleFormChange} size="small" />
                  <TextField label="Peso min. palet (kg)" name="minWeight" value={form.minWeight} onChange={handleFormChange} size="small" />
                  <TextField label="N palets pedido" name="orderedPallets" value={form.orderedPallets} onChange={handleFormChange} size="small" />
                  <TextField label="Total ampliacion" name="totalExtension" value={form.totalExtension} onChange={handleFormChange} size="small" />
                </Box>
                <Box className="quality-checks-panel">
                  <QualityCheckToggle
                    label="Estado envase"
                    value={form.packagingStatus}
                    onChange={(value) => setForm((currentForm) => ({ ...currentForm, packagingStatus: value }))}
                  />
                  <QualityCheckToggle
                    label="Etiqueta de la malla"
                    value={form.meshLabelStatus}
                    onChange={(value) => setForm((currentForm) => ({ ...currentForm, meshLabelStatus: value }))}
                  />
                  <QualityCheckToggle
                    label="Etiqueta de la caja"
                    value={form.boxLabelStatus}
                    onChange={(value) => setForm((currentForm) => ({ ...currentForm, boxLabelStatus: value }))}
                  />
                </Box>
              </QualitySection>
            </Grid>

            <Grid size={{ xs: 12, xl: 5 }}>
              <QualitySection title="3. Fotos de etiquetas" icon={<ImageRoundedIcon />}>
                <Typography className="quality-section-copy" sx={{ mb: 1.5 }}>Puedes guardar el borrador sin fotos o con una sola etiqueta, trabajar en otro pedido y volver para añadir la que falta. El control seguira editable mientras no lo envies a revision.</Typography>
                <Box className="quality-photo-grid">
                  <PhotoDropzone
                    key={`${selectedLine.id}:mesh`}
                    title="Etiqueta de la malla"
                    value={photos.meshLabel}
                    onChange={(photo) => handlePhotoChange('meshLabel', photo)}
                    onUpload={canUpload ? (file) => handlePhotoUpload(file, 'etiqueta_malla') : undefined}
                  />
                  <PhotoDropzone
                    key={`${selectedLine.id}:box`}
                    title="Etiqueta de la caja"
                    value={photos.boxLabel}
                    onChange={(photo) => handlePhotoChange('boxLabel', photo)}
                    onUpload={canUpload ? (file) => handlePhotoUpload(file, 'etiqueta_caja') : undefined}
                  />
                </Box>
              </QualitySection>
            </Grid>

            <Grid size={{ xs: 12, xl: 7 }}>
              <QualitySection title="Tabla de muestreo" icon={<FactCheckRoundedIcon />} className="quality-sampling-section">
                <Box className="quality-sampling-compact-wrap">
                  <table className="quality-sampling-compact">
                    <thead>
                      <tr>
                        <th>Confeccion</th>
                        <th>Peso ud venta</th>
                        <th>N palets/pedido</th>
                        <th>N muestras</th>
                      </tr>
                    </thead>
                    <tbody>
                      {samplingRules.map((rule, index) => (
                        <tr key={`${rule.format || 'row'}-${rule.weight}-${rule.pallets}`}>
                          {rule.format && <td rowSpan={rule.rowSpan} className="quality-sampling-group">{rule.format}</td>}
                          <td>{rule.weight}</td>
                          <td>{rule.pallets}</td>
                          <td>{rule.samples}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </QualitySection>
            </Grid>

            <Grid size={{ xs: 12, xl: 7 }}>
              <QualitySection title="5. Observaciones" icon={<FactCheckRoundedIcon />}>
                <Box className="quality-notes-grid">
                  <TextField
                    label="Observ. de linea"
                    name="lineNotes"
                    value={form.lineNotes}
                    onChange={handleFormChange}
                    size="small"
                    multiline
                    minRows={4}
                    placeholder="Escribe la observacion de linea..."
                  />
                  <TextField
                    label="Observaciones de calidad"
                    name="qualityNotes"
                    value={form.qualityNotes}
                    onChange={handleFormChange}
                    size="small"
                    multiline
                    minRows={4}
                    placeholder="Escribe las observaciones de calidad..."
                  />
                </Box>
              </QualitySection>
            </Grid>

            <Grid size={{ xs: 12, xl: 5 }}>
              <QualitySection title="6. Responsable y firma" icon={<DrawRoundedIcon />}>
                <Box className="quality-responsible-grid">
                  <TextField size="small" label="Responsable" value={form.qualityOwner} slotProps={{ input: { readOnly: true } }} />
                  <Box className="quality-reviewer-info" role="group" aria-label="Revisor responsable">
                    <PersonSearchRoundedIcon />
                    <Box>
                      <Typography className="quality-reviewer-label">Revisor responsable</Typography>
                      <Typography className="quality-reviewer-name">
                        {form.reviewer || (currentControl?.statusCode === 'COMPLETED' ? 'Revision registrada' : currentControl?.statusCode === 'CANCELLED' ? 'Control cancelado' : '')}
                      </Typography>
                      <Typography className="quality-reviewer-hint">
                        {currentControl?.statusCode === 'COMPLETED' ? 'Control validado.' : currentControl?.statusCode === 'CANCELLED' ? 'Este control está cerrado.' : 'Falta que un usuario revisor valide este control.'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box
                  component="button"
                  type="button"
                  disabled={editingBlocked}
                  aria-label={signatureDataUrl ? 'Modificar firma del responsable' : 'Firmar control'}
                  className={`quality-signature-box${signatureDataUrl ? ' quality-signature-box-signed' : ''}`}
                  onClick={() => !editingBlocked && setSignatureDialogOpen(true)}
                >
                  {signatureDataUrl ? (
                    <>
                      <img src={signatureDataUrl} alt="Firma del responsable" />
                      <Typography component="span">Modificar firma</Typography>
                    </>
                  ) : (
                    <>
                      <DrawRoundedIcon />
                      <Typography component="span">Firmar en pantalla</Typography>
                    </>
                  )}
                </Box>
                {!editingBlocked ? <Typography className="quality-section-copy" sx={{ mt: 1 }}>Puedes firmar sin completar antes los campos ni adjuntar las etiquetas. La firma se guardara con el borrador del control.</Typography> : null}
              </QualitySection>
            </Grid>
          </Grid>
          </Box>
          </>
          )}
          </Box>
        </Grid>
      </Grid>

      {canWrite ? <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} className="quality-actions">
        {!controlIsCompleted ? <Button variant="outlined" color="warning" startIcon={<WarningAmberRoundedIcon />} onClick={() => handleSaveControl('WITH_INCIDENT', 'INCIDENT')} disabled={saveBlocked}>Registrar incidencia</Button> : null}
        {!controlIsCompleted ? <Button variant="outlined" startIcon={<PersonSearchRoundedIcon />} onClick={() => handleSaveControl('PENDING_REVIEW', 'REVIEW')} disabled={saveBlocked}>Marcar para revision</Button> : null}
        <Button variant="outlined" startIcon={<PictureAsPdfRoundedIcon />} onClick={handleGeneratePdf} disabled={!selectedLine.id || selectionLoading}>Generar PDF</Button>
        <Button className="quality-primary-action" variant="contained" startIcon={<SaveRoundedIcon />} onClick={() => handleSaveControl()} disabled={saveBlocked}>{controlIsCompleted ? 'Guardar cambios' : 'Guardar borrador'}</Button>
      </Stack> : null}

      <SourceLineDialog
        open={sourceLineDialogOpen}
        form={sourceLineForm}
        saving={saving}
        onChange={handleSourceLineFormChange}
        onClose={() => setSourceLineDialogOpen(false)}
        onSubmit={handleCreateSourceLine}
      />

      <SignatureDialog
        open={signatureDialogOpen}
        value={signatureDataUrl}
        onClose={() => setSignatureDialogOpen(false)}
        onSave={setSignatureDataUrl}
      />
    </Box>
  )
}
